import { Field, Formik, FormikErrors } from 'formik';
import { DealFundingLender, DealStatusView } from '../../shared/models';
import { EnumUtils, FormikUtils, NumberUtils } from '../../shared/utils';
import {
	CerebroDatePicker,
	CerebroDrawer,
	CerebroDropdown,
	CerebroDropdownMenuItem,
	CerebroForm,
	CerebroLabel,
	CerebroMaskedInput,
	CerebroPrimaryButton,
	CerebroText,
} from '../../shared/components';
import { Grid } from '@mui/material';
import { useMutation, useQueryClient } from 'react-query';
import { getApiResponseData } from '../../shared/utils/axios';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { useApi } from '../../shared/context';
import { useSnackbar } from 'notistack';
import { QueryKeys, useFinderFeeAgreementsQuery } from '../../shared/hooks';
import { useParams } from 'react-router-dom';
import { EmptyGuid } from '../../shared/constants';
import { FormEvent, useState } from 'react';
import { OnChangeTarget } from '../../shared/utils/formik';
import { validateDateWithinPlusMinus100Years } from '../../shared/utils/date';

interface FundingLenderDrawerProps {
	fundingLender: DealFundingLender | null;
	dealStatusData: DealStatusView;
	onClose: () => void;
}

export const FundingLenderDrawer: React.FC<FundingLenderDrawerProps> = ({ fundingLender, dealStatusData, onClose }) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();
	const { guid } = useParams<'guid'>();

	const [bankContactGuid, setBankContactGuid] = useState<string | null>(null);
	const agreementQuery = useFinderFeeAgreementsQuery(bankContactGuid, {
		enabled: bankContactGuid != null && bankContactGuid != EmptyGuid && fundingLender != null,
		retry: false,
	});

	const statusUpdateMutation = useMutation(
		async ({ values }: FundingLenderMutationVariables) => {
			const patchValues = {
				fundingLenderId: values.fundingLenderId,
				finderFeeAgreementGuid: values.finderFeeAgreementGuid,
				amountFunded: NumberUtils.ParseDecimal(values.amountFunded),
				dateFunded: values.dateFunded,
			};
			const fundingLenderId = fundingLender?.id ?? 0;

			if (fundingLenderId == 0) {
				const { data, error } = await getApiResponseData<DealFundingLender>(
					api.post(CerebroApiEndpoints.Deals.AddFundingLender(guid), patchValues)
				);

				if (error) {
					throw new Error(error.response?.data);
				}

				return data;
			}

			const { data, error } = await getApiResponseData<DealFundingLender>(
				api.patch(CerebroApiEndpoints.Deals.SpecificFundingLender(guid, fundingLender?.guid), patchValues)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: () => {
				client.invalidateQueries(QueryKeys.Deal.UpdateStatus(guid));
				client.invalidateQueries(QueryKeys.Deal.Root(guid));
				snackbar.enqueueSnackbar('Funding lender successfully updated.', { variant: 'success' });
				onClose();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
		}
	);

	return (
		<CerebroDrawer open={fundingLender != null} onClose={onClose}>
			<Formik
				enableReinitialize={true}
				initialValues={
					{
						amountFunded: getFundingLenderAmount(fundingLender, dealStatusData),
						fundingLenderId: fundingLender?.bankContactGuid ?? EmptyGuid,
						finderFeeAgreementGuid: fundingLender?.finderFeeAgreement?.guid ?? EmptyGuid,
						dateFunded: fundingLender?.dateFunded ?? null,
					} as FundingLenderFormValues
				}
				validate={validateForm}
				onSubmit={FormikUtils.MutateOnSubmit(statusUpdateMutation.mutate)}
			>
				{({ isSubmitting, errors, touched, handleSubmit, setFieldValue }) => (
					<CerebroForm onSubmit={handleSubmit}>
						<Grid
							container
							spacing={3}
							sx={{ marginTop: 3 }}
							justifyContent={{ xs: 'center', md: 'flex-start' }}
						>
							<Grid item xs={11}>
								<CerebroLabel htmlFor="dateFunded" sx={{ marginTop: 3 }}>
									Date Funded
								</CerebroLabel>
								<Grid container spacing={3}>
									<Grid item xs={12}>
										<Field
											id="dateFunded"
											component={CerebroDatePicker}
											name="dateFunded"
											error={touched.dateFunded && errors.dateFunded}
										/>
										{touched.dateFunded && errors.dateFunded != null && (
											<CerebroText variant="error">{errors.dateFunded}</CerebroText>
										)}
									</Grid>
								</Grid>
								<CerebroLabel htmlFor="amountFunded" sx={{ marginTop: 3 }}>
									Amount Funded
								</CerebroLabel>
								<Grid container spacing={3}>
									<Grid item xs={12}>
										<Field
											id="amountFunded"
											as={CerebroMaskedInput}
											name="amountFunded"
											disabled={isSubmitting}
											error={touched.amountFunded && errors.amountFunded}
										/>
										{touched.amountFunded && errors.amountFunded != null && (
											<CerebroText variant="error">{errors.amountFunded}</CerebroText>
										)}
									</Grid>
								</Grid>
								<CerebroLabel htmlFor="fundingLenderId" sx={{ marginTop: 3 }}>
									Funding Lender
								</CerebroLabel>
								<Grid container spacing={3}>
									<Grid item xs={12}>
										<Field
											id="fundingLenderId"
											as={CerebroDropdown}
											name="fundingLenderId"
											disabled={isSubmitting}
											error={touched.fundingLenderId && errors.fundingLenderId}
											onChange={(event: FormEvent) => {
												const target = event.target as OnChangeTarget;
												setBankContactGuid(target.value);
												setFieldValue('fundingLenderId', target.value);
											}}
										>
											{dealStatusData.activeLenders.map(lender => (
												<CerebroDropdownMenuItem
													key={lender.bankContactGuid}
													value={lender.bankContactGuid}
												>
													{lender.fullName} ({lender.bankName})
												</CerebroDropdownMenuItem>
											))}
										</Field>
										{touched.fundingLenderId && errors.fundingLenderId != null && (
											<CerebroText variant="error">{errors.fundingLenderId}</CerebroText>
										)}
									</Grid>
								</Grid>
								<CerebroLabel htmlFor="finderFeeAgreementGuid" sx={{ marginTop: 3 }}>
									Finder Fee Agreement
								</CerebroLabel>
								<Grid container spacing={3}>
									<Grid item xs={12}>
										<Field
											id="finderFeeAgreementGuid"
											as={CerebroDropdown}
											name="finderFeeAgreementGuid"
											disabled={isSubmitting}
											error={touched.finderFeeAgreementGuid && errors.finderFeeAgreementGuid}
										>
											{agreementQuery.data?.map(agreement => (
												<CerebroDropdownMenuItem key={agreement.guid} value={agreement.guid}>
													{agreement.scope} |{' '}
													{EnumUtils.GetTier3ContactTypeDescription(
														agreement.tier3ContactType
													)}
												</CerebroDropdownMenuItem>
											))}
										</Field>
										{touched.finderFeeAgreementGuid && errors.finderFeeAgreementGuid != null && (
											<CerebroText variant="error">{errors.finderFeeAgreementGuid}</CerebroText>
										)}
									</Grid>
								</Grid>
							</Grid>
						</Grid>

						<CerebroPrimaryButton type="submit" loading={isSubmitting} sx={{ marginTop: 6 }}>
							Save
						</CerebroPrimaryButton>
					</CerebroForm>
				)}
			</Formik>
		</CerebroDrawer>
	);
};


const validateForm = (values: FundingLenderFormValues): FundingLenderFormErrors => {
	const errors: FundingLenderFormErrors = {};

	if (!values.fundingLenderId || values.fundingLenderId == EmptyGuid) {
		errors.fundingLenderId = 'You must select the lender.';
	}
	const amountFunded = NumberUtils.ParseDecimal(values.amountFunded);
	if (!amountFunded || amountFunded <= 0) {
		errors.amountFunded = 'Valid funded amount is required.';
	}

	if (!values.dateFunded) {
		errors.dateFunded = 'Must select a valid date funded.';
	} else {
		const dateError = validateDateWithinPlusMinus100Years(values.dateFunded, 'Date Funded');
		if (dateError) {
			errors.dateFunded = dateError;
		}
	}
	
	return errors;
};

type FundingLenderFormErrors = FormikErrors<FundingLenderFormValues>;
type FundingLenderMutationVariables = FormikUtils.MutateOnSubmitVariables<FundingLenderFormValues>;
interface FundingLenderFormValues {
	amountFunded: string;
	fundingLenderId: string;
	finderFeeAgreementGuid: string | null;
	dateFunded: Date | null;
}

function getFundingLenderAmount(fundingLender: DealFundingLender | null, dealStatusData: DealStatusView): string {
	if (fundingLender != null && fundingLender.amountFunded > 0) {
		return NumberUtils.FormatDecimal(fundingLender.amountFunded);
	}

	return NumberUtils.FormatDecimal(dealStatusData.deal.totalRequestedAmount);
}
